import { UseFormReturn } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { User, Building2, Mail, ArrowRight, UserCircle, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import PhoneInput from "@/components/PhoneInput";

const inputClasses =
  "pl-10 h-10 sm:h-12 rounded-xl bg-slate-50 border-slate-200 text-slate-900 text-base placeholder:text-slate-400 focus-visible:ring-0 focus-visible:ring-offset-0 focus:ring-2 focus:ring-red-500/20 focus:ring-offset-0 focus:border-red-500 transition-all w-full min-w-0 box-border";

interface QuickStepDetailsProps {
  form: UseFormReturn<any>;
  isSubmitting: boolean;
  onNext: () => void;
}

const QuickStepDetails = ({ form, isSubmitting, onNext }: QuickStepDetailsProps) => {
  return (
    <motion.div
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -30 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className="space-y-3 sm:space-y-6 w-full min-w-0 overflow-visible"
    >
      {/* Section header */}
      <div className="mb-1 sm:mb-2">
        <div className="flex items-center gap-2 mb-1">
          <div className="h-5 w-1 rounded-full bg-red-500" />
          <UserCircle className="h-4 w-4 text-red-600" />
          <h3 className="text-xs font-bold uppercase tracking-widest text-red-600">
            Your Details
          </h3>
        </div>
        <p className="text-slate-500 text-sm ml-3">
          Tell us a bit about yourself so we can prepare for your call.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-5">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-slate-600 font-semibold text-xs uppercase tracking-wide">
                Full Name <span className="text-red-500">*</span>
              </FormLabel>
              <FormControl>
                <div className="relative group">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 group-focus-within:text-red-500 transition-colors" />
                  <Input placeholder="John Smith" className={inputClasses} {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="companyName"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-slate-600 font-semibold text-xs uppercase tracking-wide">
                Company Name <span className="text-red-500">*</span>
              </FormLabel>
              <FormControl>
                <div className="relative group">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 group-focus-within:text-red-500 transition-colors" />
                  <Input placeholder="Acme Inc." className={inputClasses} {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-5">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-slate-600 font-semibold text-xs uppercase tracking-wide">
                Email <span className="text-red-500">*</span>
              </FormLabel>
              <FormControl>
                <div className="relative group">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 group-focus-within:text-red-500 transition-colors" />
                  <Input type="email" placeholder="john@company.com" className={inputClasses} {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="smsReminderNumber"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-slate-600 font-semibold text-xs uppercase tracking-wide">
                Phone Number <span className="text-red-500">*</span>
              </FormLabel>
              <FormControl>
                <PhoneInput
                  value={field.value}
                  onChange={field.onChange}
                  className="h-10 sm:h-12 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 text-base px-3 focus-within:border-red-500 focus-within:ring-2 focus-within:ring-red-500/20 transition-all w-full min-w-0 box-border"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="pt-1 sm:pt-2 flex justify-end">
        <Button
          type="button"
          onClick={onNext}
          disabled={isSubmitting}
          className="bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white font-bold px-8 h-10 sm:h-12 rounded-xl shadow-lg shadow-red-500/20 hover:shadow-red-500/30 transition-all duration-200"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading…
            </>
          ) : (
            <>
              Next: Book a Time
              <ArrowRight className="ml-2 h-4 w-4" />
            </>
          )}
        </Button>
      </div>
    </motion.div>
  );
};

export default QuickStepDetails;
